import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, Label,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const UserEngagementPage = () => {
  const [dauData, setDauData] = useState(null);
  const [mauData, setMauData] = useState(null);
  const [ratioData, setRatioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dauResponse, mauResponse, ratioResponse] = await Promise.all([
        axios.get(`${API}/super-admin/app-analytics/dau`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/mau`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/dau-mau-ratio`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setDauData(dauResponse.data);
      setMauData(mauResponse.data);
      setRatioData(ratioResponse.data);
    } catch (err) {
      console.error('Failed to fetch engagement data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading user engagement analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">User Engagement Metrics</h1>
          <p className="text-purple-200 text-sm">Mobile app user activity and engagement analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchAll}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const avgDau = dauData?.daily_data ? 
    Math.round(dauData.daily_data.reduce((sum, day) => sum + day.active_users, 0) / 30) : 0;
  
  const currentMau = mauData?.monthly_data && mauData.monthly_data.length > 0 ?
    mauData.monthly_data[mauData.monthly_data.length - 1].active_users : 0;
  
  const currentRatio = ratioData?.monthly_data && ratioData.monthly_data.length > 0 ?
    ratioData.monthly_data[ratioData.monthly_data.length - 1].ratio : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">User Engagement Metrics</h1>
              <p className="text-purple-200 text-sm">Mobile app user activity and engagement analytics</p>
            </div>
          </div>
          <button
            onClick={fetchAll}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Mobile Users</p>
              <p className="text-3xl font-bold text-gray-900">{dauData?.total_users || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Daily Active</p>
              <p className="text-3xl font-bold text-gray-900">{avgDau}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current MAU</p>
              <p className="text-3xl font-bold text-gray-900">{currentMau}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">DAU/MAU Ratio</p>
              <p className="text-3xl font-bold text-gray-900">{currentRatio}%</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Chart 1: Daily Active Users */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Active Users (DAU) - Last 30 Days</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={dauData?.daily_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis>
              <Label value="Active Users" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="active_users" stroke="#8b5cf6" strokeWidth={2} name="Daily Active Users" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The number of unique users who opened the mobile app each day over the last 30 days.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Consistent DAU indicates strong app adoption and daily habits. Weekday spikes with weekend drops are normal for work-related apps. Declining DAU suggests users aren't finding value or facing usability issues. Aim for steady or growing DAU as your user base expands. Sudden drops may indicate technical issues or push notification problems.
          </p>
        </div>
      </div>

      {/* Chart 2: Monthly Active Users */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Active Users (MAU) - Last 12 Months</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={mauData?.monthly_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis>
              <Label value="Active Users" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="active_users" stroke="#3b82f6" strokeWidth={2} name="Monthly Active Users" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The number of unique users who opened the mobile app at least once each month over the last 12 months.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Growing MAU indicates successful user acquisition and app retention. Flat MAU with growing total users suggests churn equals acquisition - focus on retention. Declining MAU is critical - investigate why users are abandoning the app. Compare MAU growth to organisation growth - if adding companies but MAU flat, onboarding or app value issues exist.
          </p>
        </div>
      </div>

      {/* Chart 3: DAU/MAU Ratio */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">DAU/MAU Ratio - Stickiness Metric (Last 12 Months)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={ratioData?.monthly_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis>
              <Label value="Ratio (%)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip formatter={(value) => `${value}%`} />
            <Legend />
            <Line type="monotone" dataKey="ratio" stroke="#10b981" strokeWidth={2} name="DAU/MAU Ratio" />
            <Line type="monotone" dataKey={() => 20} stroke="#94a3b8" strokeWidth={1} strokeDasharray="5 5" name="Good Benchmark (20%)" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The ratio of daily active users to monthly active users - a measure of app "stickiness" (how often monthly users engage daily).
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Above 20% is excellent (users engage frequently). 10-20% is good (moderate engagement). Below 10% means low stickiness - users download but rarely use the app. High ratio indicates the app is essential to daily workflows. Low ratio suggests it's used occasionally or for specific tasks only. Improve stickiness with push notifications, daily value features, or habit-forming mechanics.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserEngagementPage;
